import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Save, CreditCard, Info } from "lucide-react";

export default function RazorpayConfig() {
  const [cfg, setCfg] = useState(null);
  const [keyId, setKeyId] = useState("");
  const [keySecret, setKeySecret] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/razorpay/config").then((r) => { setCfg(r.data); setKeyId(r.data.key_id || ""); }).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!cfg) return <div className="mt-4 text-[#71717A]">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      const body = { key_id: keyId };
      if (keySecret) body.key_secret = keySecret;
      const { data } = await api.put("/razorpay/config", body);
      setCfg(data);
      setKeyId(data.key_id || "");
      setKeySecret("");
      toast.success("Razorpay settings saved");
    } catch (e) {
      toast.error("Failed to save Razorpay settings");
    } finally {
      setSaving(false);
    }
  };

  const isLive = cfg.mode === "live";
  const badge = cfg.mode === "unset"
    ? { text: "Not Configured", cls: "bg-white/10 text-[#A1A1AA]" }
    : isLive
      ? { text: "LIVE MODE", cls: "bg-[#25D366]/20 text-[#25D366]" }
      : { text: "TEST MODE", cls: "bg-[#FF4500]/20 text-[#FF7043]" };

  return (
    <div className="rounded-2xl border border-border bg-[#121212] p-6 mt-4">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-display text-2xl font-800 uppercase tracking-tight flex items-center gap-2">
          <CreditCard className="h-6 w-6 text-[#FF4500]" /> Payment (Razorpay)
        </h2>
        <span data-testid="rzp-mode-badge" className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${badge.cls}`}>{badge.text}</span>
      </div>
      <p className="text-sm text-[#71717A] mb-6">Enter your Razorpay API keys. Use <span className="text-[#FF7043]">rzp_test_…</span> keys for testing and <span className="text-[#25D366]">rzp_live_…</span> keys to accept real payments.</p>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-[#A1A1AA]">Key ID <span className="text-[#FF4500]">*</span></Label>
          <Input data-testid="rzp-key-id-input" value={keyId} onChange={(e) => setKeyId(e.target.value)} placeholder="rzp_live_XXXXXXXXXXXX" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-[#A1A1AA]">Key Secret {cfg.key_secret_set ? "(saved)" : ""} <span className="text-[#FF4500]">*</span></Label>
          <Input data-testid="rzp-key-secret-input" type="password" value={keySecret} onChange={(e) => setKeySecret(e.target.value)} placeholder={cfg.key_secret_set ? "•••••••• (leave blank to keep current)" : "Enter key secret"} />
        </div>
      </div>

      <div className="mt-5 rounded-lg border border-border bg-[#0A0A0A] p-3 flex items-start gap-2">
        <Info className="h-4 w-4 text-[#FF4500] mt-0.5 shrink-0" />
        <p className="text-xs text-[#A1A1AA]">
          Get keys from <span className="text-[#E4E4E7]">Razorpay Dashboard → Settings → API Keys</span>. Live keys require completed KYC/account activation. The "Test Mode" banner on checkout disappears automatically once live keys are saved.
        </p>
      </div>

      <div className="mt-6 flex justify-end">
        <Button data-testid="rzp-save-button" onClick={save} disabled={saving || !keyId} className="rounded-full bg-[#FF4500] hover:bg-[#FF5722] h-11">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save Settings
        </Button>
      </div>
    </div>
  );
}
